import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getService } from "../../../reducers/service";
import { notify } from "../../../reducers/NotifierReducer";
import { setCircularLoading } from "../../../reducers/UiReducer";
import { FLSAOptions, RehireOptions } from "../../../utils/enums";
import { serviceNames } from "../../../utils/constants";
import { PATH_EMPLOYEE } from "../../../utils/routeConstants";
import {
  formatToTwoDecimals,
  mapDictionaryOptions,
} from "../../../utils/functions";
import { dateTimePicker } from "../../../reducers/TimeReducer";

export const useEmployeeData = (id, reset) => {
  const dispatch = useDispatch();
  const userAgency = useSelector((state) => state.user.userAgency);
  const user = useSelector((state) => state.user.userData.user);
  const history = useHistory();
  const dictionary = useSelector((state) => state.dictionary.dictionary);

  const [personName, setPersonName] = useState(null);
  const [latestAssignmentData, setLatestAssignmentData] = useState({});
  const flsaStatusOptions = [
    { label: FLSAOptions.EXEMPT, value: FLSAOptions.EXEMPT },
    { label: FLSAOptions.NON_EXEMPT, value: FLSAOptions.NON_EXEMPT },
  ];
  const rehireOptions = [
    { label: RehireOptions.OK, value: RehireOptions.OK },
    { label: RehireOptions.NOT_OK, value: RehireOptions.NOT_OK },
  ];
  const [loaded, setLoaded] = useState(false);
  const [oldEmployeeNum, setOldEmployeeNum] = useState(null);

  const raceOptions = mapDictionaryOptions(dictionary.codeRace);
  const genderOptions = mapDictionaryOptions(dictionary.codeSex);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);
  const suffixOptions = mapDictionaryOptions(dictionary.codeNameSuffixes);
  const rankOptions = mapDictionaryOptions(dictionary.codePerRank);
  const countyOptions = mapDictionaryOptions(dictionary.codeCounties);
  const titlePositionOptions = mapDictionaryOptions(
    dictionary.codePerTitlePosition
  );
  const departmentOptions = mapDictionaryOptions(dictionary.codePerDepartment);
  const sectionOptions = mapDictionaryOptions(dictionary.codePerSection);
  const employmentOptions = mapDictionaryOptions(
    dictionary.codePerEmploymentStatuses
  );
  const educationOptions = mapDictionaryOptions(dictionary.codePerEducation);
  const stateOptions = mapDictionaryOptions(dictionary.codeStates);
  const maritalOptions = mapDictionaryOptions(dictionary.codeMaritalStatus);
  const bloodTypeOptions = mapDictionaryOptions(dictionary.codeBloodType);
  const cityOptions = useMemo(() => {
    return (dictionary.codeCities || []).map((g) => ({
      label: g.State ? `${g.CityDescription}, ${g.State}` : g.CityDescription,
      value: g.CityDescription,
      state: g.State,
    }));
  }, [dictionary.codeCities]);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        dispatch(setCircularLoading(true));
        setLoaded(false);

        if (id) {
          const service = getService(serviceNames.personnel);
          const resultData = await service.find({
            query: {
              id: id,
              agencyId: userAgency,
            },
          });
          const result = resultData?.data?.[0] || null;

          if (!result) {
            setTimeout(() => {
              dispatch(notify("You don't have access to this employee.", "warning"));
            }, 1000);
            history.go(-1);
            return;
          }

          if (isMounted && result) {
            reset({
              id: result.id || null,
              firstName: result.firstName || "",
              lastName: result.lastName || "",
              middleName: result.middleName || "",
              suffix: result.suffix || "",
              dob: result?.dob ? dateTimePicker(result.dob) : "",
              ssn: result.ssn || "",
              email: result.email || "",
              gender: result.gender || "",
              employeeNum: result.employeeNum || null,
              phoneNo: result.phoneNo || "",
              workPhoneNum: result.workPhoneNum || "",
              cellNo: result.cellNo || "",
              pagerNo: result.pagerNo || "",
              dl: result.dl || "",
              dlState: result.dlState || "",
              dlExpire: result.dlExpire
                ? dateTimePicker(result.dlExpire)
                : "",
              race: result.race || "",
              status: result.status || "",
              address: result.address || "",
              rCity: result.rCity || "",
              rState: result.rState || "",
              rZip: result.rZip || "",
              mAddress: result.mAddress || "",
              mCity: result.mCity || "",
              mState: result.mState || "",
              mZip: result.mZip || "",
              maritalStatus: result.maritalStatus || "",
              spouseName: result.spouseName || "",
              spouseSSN: result.spouseSSN || "",
              spouseEmployer: result.spouseEmployer || "",
              spousePhoneNumber: result.spousePhoneNumber || "",
              emergencyContact: result.emergencyContact || "",
              ecHomePhone: result.ecHomePhone || "",
              ecCellNo: result.ecCellNo || "",
              contactAddress: result.contactAddress || "",
              contactCity: result.contactCity || "",
              contactState: result.contactState || "",
              contactZip: result.contactZip || "",
              medicalNotes: result.medicalNotes || "",
              bloodType: result.bloodType || "",
              relationship: result.relationship || "",
              positionNum: result.positionNum || "",
              masterCostCenter: result.masterCostCenter || "",
              salary: result.salary ? formatToTwoDecimals(result.salary) : "",
              annualSalary: result.annualSalary
                ? formatToTwoDecimals(result.annualSalary)
                : "",
              titlePosition: result.titlePosition || "",
              rank: result.rank || "",
              classCode: result.classCode || "",
              grade: result.grade || "",
              personnelNum: result.personnelNum || "",
              personnelArea: result.personnelArea || "",
              subArea: result.subArea || "",
              orgUnit: result.orgUnit || "",
              contract: result.contract || "",
              positionStartDate: result.positionStartDate
                ? dateTimePicker(result.positionStartDate)
                : "",
              positionEndDate: result.positionEndDate
                ? dateTimePicker(result.positionEndDate)
                : "",
              dateLwopMilitary: result.dateLwopMilitary
                ? dateTimePicker(result.dateLwopMilitary)
                : "",
              dateReturnedPayroll: result.dateReturnedPayroll
                ? dateTimePicker(result.dateReturnedPayroll)
                : "",
              perfRevDate: result.perfRevDate
                ? dateTimePicker(result.perfRevDate)
                : "",
              careerServiceDate: result.careerServiceDate
                ? dateTimePicker(result.careerServiceDate)
                : "",
              leaveAccDate: result.leaveAccDate
                ? dateTimePicker(result.leaveAccDate)
                : "",
              careerServicePayment: result.careerServicePayment
                ? formatToTwoDecimals(result.careerServicePayment)
                : "",
              orderNum: result.orderNum || "",
              percentage: result.percentage || "",
              internalCC: result.internalCC || "",
              wbsElement: result.wbsElement || "",
              wbscc: result.wbscc || "",
              otherCC: result.otherCC || "",
              orgChart: result.orgChart || false,
              flsaStatus:
                result.flsaStatus === true
                  ? "Non Exempt"
                  : result.flsaStatus === false
                  ? "Exempt"
                  : null,
              cgdg: result.cgdg || false,
              clip: result.clip || false,
              commissionedDate: result.commissionedDate
                ? dateTimePicker(result.commissionedDate)
                : "",
              trooperDate: result.trooperDate
                ? dateTimePicker(result.trooperDate)
                : "",
              latestHireDate: result.latestHireDate
                ? dateTimePicker(result.latestHireDate)
                : "",
              hireDate: result.hireDate
                ? dateTimePicker(result.hireDate)
                : "",
              badgeNum: result.badgeNum || "",
              department: result.department || "",
              departmentDate: result.departmentDate
                ? dateTimePicker(result.departmentDate)
                : "",
              section: result.section || "",
              sectionDate: result.sectionDate
                ? dateTimePicker(result.sectionDate)
                : "",
              residentialCounty: result.residentialCounty || "",
              assignedCounty: result.assignedCounty || "",
              education: result.education || "",
              notes: result.notes || "",
              militaryNotes: result.militaryNotes || "",
              dropDate: result.dropDate
                ? dateTimePicker(result.dropDate)
                : "",
              retireDate: result.retireDate
                ? dateTimePicker(result.retireDate)
                : "",
              separationDate: result.separationDate
                ? dateTimePicker(result.separationDate)
                : "",
              separationCode: result.separationCode || "",
              separationComments: result.separationComments || "",
              rehire:
                result.rehire === 1
                  ? "OK to rehire"
                  : result.rehire === 2
                  ? "Do not rehire"
                  : null,
            });
            const personName = `${result.firstName}${
              result.middleName ? ` ${result.middleName.charAt(0)}.` : ""
            } ${result.lastName}`;

            setLatestAssignmentData({
              trooperDate: result.trooperDate
                ? dateTimePicker(result.trooperDate)
                : "",
              department: result.department || "",
              departmentDate: result.departmentDate
                ? dateTimePicker(result.departmentDate)
                : "",
              section: result.section || "",
              sectionDate: result.sectionDate
                ? dateTimePicker(result.sectionDate)
                : "",
              titlePosition: result.titlePosition || "",
              rank: result.rank || "",
            });

            setPersonName(personName);
            setOldEmployeeNum(result.employeeNum || null);
          }
        }
      } catch (error) {
        if (isMounted) {
          dispatch(notify("Failed to fetch employee", "error"));
          history.push(PATH_EMPLOYEE);
        }
      } finally {
        dispatch(setCircularLoading(false));
        setLoaded(true);
      }
    };

    init();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch, reset, userAgency]);

  return {
    personName,
    genderOptions,
    raceOptions,
    employmentOptions,
    stateOptions,
    maritalOptions,
    bloodTypeOptions,
    flsaStatusOptions,
    tagTypeOptions,
    countyOptions,
    rankOptions,
    rehireOptions,
    titlePositionOptions,
    departmentOptions,
    sectionOptions,
    educationOptions,
    suffixOptions,
    cityOptions,
    oldEmployeeNum,
    latestAssignmentData,
    loaded,
    userAgency,
    user,
    dictionary,
  };
};
